import React, { useState, useEffect, useMemo } from 'react';
import { tradingApi, Order, OrdersResponse } from '../services/api';
import ErrorDisplay from './ErrorDisplay';
import { formatTimeET } from '../utils/datetime';
import { formatCurrency } from '../utils/currency';
import { DataTable, Column } from './DataTable';
import { Pencil } from 'lucide-react';
import OrderSymbolFilter, { matchesOrderSymbol } from './OrderSymbolFilter';

interface OpenOrdersProps {
  refreshInterval?: number;
  onEditOrder?: (order: Order) => void;
  securityTypeFilter?: string;
}

const OpenOrders: React.FC<OpenOrdersProps> = ({
  refreshInterval = 30000,
  onEditOrder,
  securityTypeFilter = 'ALL',
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10; // Increased page size for better oversight
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorHeader, setErrorHeader] = useState<string>("Failed to load data");
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [snapshotStatus, setSnapshotStatus] = useState({ stale: false, available: true, connectionStatus: 'connected' });
  const hasFetchedOrdersRef = React.useRef(false);
  const currentPageRef = React.useRef(page);
  currentPageRef.current = page;

  const fetchOrders = React.useCallback(async (isInitialLoad = false, pageOverride?: number) => {
    const hasData = hasFetchedOrdersRef.current;
    if (isInitialLoad || !hasData) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    setError(null);

    try {
      if (isInitialLoad) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const response: OrdersResponse = await tradingApi.getOpenOrders(
        pageOverride ?? currentPageRef.current,
        pageSize,
        securityTypeFilter,
      );

      if (response.success && response.data) {
        setOrders(response.data?.orders || []);
        setSnapshotStatus({
          stale: !!response.data.stale,
          available: response.data.snapshot_available !== false,
          connectionStatus: response.data.connection_status || 'connected',
        });
      } else {
        setOrders([]);
        if (response.error) {
          setError(response.error);
          setErrorHeader("Failed to load open orders");
        }
      }

      const ts = response.timestamp || new Date().toISOString();
      setLastUpdated(formatTimeET(ts));
      hasFetchedOrdersRef.current = true;
    } catch (err) {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setError(err instanceof Error ? err.message : 'Failed to fetch open orders');
      setErrorHeader("Failed to load open orders");
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pageSize, securityTypeFilter]);

  useEffect(() => {
    const handleRefresh = () => fetchOrders(false, page);
    window.addEventListener('trading-data-refresh', handleRefresh);
    return () => window.removeEventListener('trading-data-refresh', handleRefresh);
  }, [fetchOrders, page]);

  useEffect(() => {
    fetchOrders(true, 1);
  }, [fetchOrders]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchOrders(false, page);
    };
    const intervalId = setInterval(refreshWhenVisible, refreshInterval);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshWhenVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshWhenVisible);
    };
  }, [fetchOrders, page, refreshInterval]);

  useEffect(() => {
    if (snapshotStatus.connectionStatus !== 'initializing') return;
    const retryId = window.setTimeout(() => fetchOrders(false, page), 2000);
    return () => window.clearTimeout(retryId);
  }, [fetchOrders, page, snapshotStatus.connectionStatus]);

  const getSideBadge = (side: string) => {
    const variants: Record<string, string> = {
      BUY: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
      SELL: 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-black tracking-widest uppercase rounded ${variants[side] || 'bg-white/10 text-white/70 border border-white/20'}`}>
        {side}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      Submitted: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
      PreSubmitted: 'bg-white/10 text-white/50 border border-white/10',
      Cancelled: 'bg-rose-500/20 text-rose-300 border border-rose-500/30',
      Filled: 'bg-emerald-500/30 text-emerald-50 border border-emerald-500/50',
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 text-[10px] font-black tracking-[0.15em] uppercase rounded-sm shadow-sm ${variants[status] || 'bg-white/5 text-white/40 border border-white/10'}`}>
        {status}
      </span>
    );
  };

  const getOrderTypeBadge = (orderType: string) => {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-black bg-white/10 border border-white/20 text-white/90 uppercase tracking-tighter">
        {orderType}
      </span>
    );
  };

  const getDisplayPrice = (order: Order) => {
    const type = (order.order_type || '').toUpperCase();
    const currency = order.currency || 'USD';

    if (type === 'MKT') return <span className="text-white/30 font-black tracking-tighter uppercase italic text-[13px]">Market</span>;
    if (type === 'MIDPRICE') return <span className="text-cyan-300 font-black tracking-tighter uppercase italic text-[13px]">Midpoint</span>;
    if (type === 'STP') return <span className="text-rose-400 font-mono text-[14px] font-bold">{formatCurrency(order.stop_price, currency)}</span>;
    if (type === 'LMT') return <span className="text-emerald-400 font-mono text-[14px] font-bold">{formatCurrency(order.limit_price, currency)}</span>;
    if (type === 'TRAIL') {
      return (
        <div className="flex flex-col leading-none font-mono">
          <span className="text-rose-400 text-[14px] font-bold">
            {order.trailing_stop_price ? formatCurrency(order.trailing_stop_price, currency) : 'Pending'}
          </span>
          <span className="text-[10px] text-amber-400 font-black tracking-tighter italic">
            Trail: {order.trailing_percent ? `${order.trailing_percent}%` : formatCurrency(order.stop_price, currency)}
          </span>
        </div>
      );
    }
    if (type === 'STPLMT') {
      return (
        <div className="flex flex-col leading-none font-mono">
          <span className="text-emerald-400 text-[14px] font-bold">{formatCurrency(order.limit_price, currency)}</span>
          <span className="text-[10px] text-rose-400 font-black tracking-tighter italic">STP: {formatCurrency(order.stop_price, currency)}</span>
        </div>
      );
    }
    return <span className="font-mono text-[14px] font-bold text-white">{formatCurrency(order.limit_price || order.stop_price || 0, currency)}</span>;
  };

  const getDisplaySymbol = (order: Order) => {
    if ((order.secType || '').toUpperCase() === 'FUT') {
      return order.localSymbol || (order.symbol && order.lastTradeDateOrContractMonth ? `${order.symbol} ${order.lastTradeDateOrContractMonth}` : order.symbol);
    }
    return order.symbol;
  };

  const getOriginBadge = (origin?: string | null) => {
    const normalized = (origin || 'UNKNOWN').toUpperCase();
    const variants: Record<string, string> = {
      LOCAL: 'border border-neon text-neon bg-neon/10 shadow-[0_0_15px_rgba(0,255,153,0.2)]',
      RECOVERED: 'border border-rose-500 text-rose-300 bg-rose-500/20',
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 text-[10px] font-black uppercase rounded-sm ${variants[normalized] || 'border border-white/20 text-white/60 bg-white/10'}`}>
        {normalized === 'LOCAL' ? 'SYSTEM' : normalized}
      </span>
    );
  };

  const handleCancelOrder = async (orderId: number) => {
    if (!orderId || cancellingId !== null) return;
    setCancellingId(orderId);
    setError(null);
    try {
      const response = await tradingApi.cancelOrder(orderId);
      if (!response.success) {
        setError(response.error || `Failed to cancel order ${orderId}`);
        setErrorHeader("Order Cancellation Failed");
      } else {
        setOrders(prev =>
          prev.map(order =>
            order.order_id === orderId
              ? { ...order, status: 'Cancelled', remaining_quantity: 0 }
              : order
          )
        );
        window.dispatchEvent(new CustomEvent('trading-data-refresh'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to cancel order ${orderId}`);
      setErrorHeader("Order Cancellation Failed");
    } finally {
      setCancellingId(null);
    }
  };

  const handleEditOrder = (order: Order) => {
    if ((order.status || '').toUpperCase() === 'CANCELLED') return;
    onEditOrder?.(order);
    window.setTimeout(() => {
      document.getElementById('manual-order-entry')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  const orderColumns: Column<Order>[] = [
    {
      id: 'order_id',
      header: 'ID',
      accessor: 'order_id',
      sortable: true,
      render: (order) => (
        <div className="flex items-center gap-2">
          {order.parent_id && order.parent_id !== 0 && (
            <span className="text-neon/60 ml-1">└──</span>
          )}
          <span className="font-mono text-white/40 text-[11px] font-bold tracking-tight">{order.order_id}</span>
        </div>
      )
    },
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: (o) => o.symbol || '',
      sortable: true,
      render: (order) => (
        <div className="flex flex-col">
          <span className="font-semibold text-[12px] tracking-tight text-white">{getDisplaySymbol(order)}</span>
          <span className="text-[10px] text-white/30 font-black uppercase tracking-widest">{order.secType || 'STK'}</span>
        </div>
      )
    },
    {
      id: 'origin',
      header: 'Origin',
      accessor: (o) => `${o.origin || ''} ${o.strategy || ''}`,
      sortable: true,
      render: (order) => (
        <div className="flex flex-col gap-1.5">
          {getOriginBadge(order.origin)}
          {order.strategy && <span className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{order.strategy}</span>}
        </div>
      )
    },
    {
      id: 'side',
      header: 'Side',
      accessor: 'side',
      sortable: true,
      render: (order) => getSideBadge(order.side)
    },
    {
      id: 'order_type',
      header: 'Type',
      accessor: 'order_type',
      sortable: true,
      render: (order) => getOrderTypeBadge(order.order_type)
    },
    {
      id: 'quantity',
      header: 'Size',
      accessor: 'quantity',
      sortable: true,
      render: (order) => (
        <div className="flex flex-col font-mono leading-tight">
          <span className="font-black text-white text-[14px]">{order.quantity?.toLocaleString()}</span>
          <div className="flex gap-2 text-[10px] font-bold mt-0.5">
            <span className="text-emerald-400">F: {order.filled_quantity?.toLocaleString() ?? 0}</span>
            <span className="text-white/20">R: {order.remaining_quantity?.toLocaleString() ?? order.quantity?.toLocaleString()}</span>
          </div>
        </div>
      )
    },
    {
      id: 'price',
      header: 'Price',
      accessor: (o) => o.limit_price || o.stop_price || 0,
      sortable: true,
      render: (order) => <div className="font-mono font-black">{getDisplayPrice(order)}</div>
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      render: (order) => getStatusBadge(order.status || 'Unknown')
    },
    {
      id: 'timestamp',
      header: 'Time',
      accessor: 'timestamp',
      sortable: true,
      align: 'right',
      render: (order) => (
        <span className="text-white/60 font-mono font-bold text-[12px]">
          {formatTimeET(order.timestamp)}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'order_id',
      align: 'right',
      render: (order) => (
        <div className="flex items-center justify-end gap-2">
          {onEditOrder && (
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-neon/30 text-neon bg-neon/10 hover:bg-neon/20 active:scale-95 transition-all disabled:opacity-30"
              disabled={
                refreshing ||
                cancellingId !== null ||
                (order.status || '').toUpperCase() === 'CANCELLED'
              }
              onClick={(e) => {
                e.stopPropagation();
                handleEditOrder(order);
              }}
              title="Edit this order in Manual Order"
            >
              <Pencil className="w-3 h-3" />
              Edit
            </button>
          )}
          <button
            className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-rose-500/30 text-rose-200 bg-rose-500/10 hover:bg-rose-500/20 active:scale-95 transition-all disabled:opacity-30"
            disabled={
              refreshing ||
              cancellingId === order.order_id ||
              (order.status || '').toUpperCase() === 'CANCELLED'
            }
            onClick={(e) => {
              e.stopPropagation();
              handleCancelOrder(order.order_id);
            }}
          >
            {cancellingId === order.order_id ? 'Wait' : 'Cancel'}
          </button>
        </div>
      )
    }
  ];

  const displayOrders = useMemo(() => {
    const filteredOrders = securityTypeFilter === 'ALL'
      ? orders
      : orders.filter((order) => (order.secType || 'STK').toUpperCase() === securityTypeFilter);
    const symbolFilteredOrders = filteredOrders.filter((order) => matchesOrderSymbol(order, symbolFilter));
    const topLevel = symbolFilteredOrders.filter(o => !o.parent_id || o.parent_id === 0);
    const children = symbolFilteredOrders.filter(o => o.parent_id && o.parent_id !== 0);

    const result: Order[] = [];
    topLevel.forEach(parent => {
      result.push(parent);
      const myChildren = children.filter(c => c.parent_id === parent.order_id);
      result.push(...myChildren);
    });

    const orphans = children.filter(c => !topLevel.find(p => p.order_id === c.parent_id));
    result.push(...orphans);

    return result;
  }, [orders, securityTypeFilter, symbolFilter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <h3 className="text-sm font-bold text-neon tracking-tight uppercase mb-0.5">Open Orders</h3>
            <div className="flex items-center gap-2 text-[10px] text-muted font-bold opacity-30 uppercase tracking-[0.2em]">
              {refreshing
                ? <span className="animate-pulse text-neon">Refreshing...</span>
                : <span>{snapshotStatus.connectionStatus === 'initializing' ? 'Synchronizing' : snapshotStatus.stale ? 'Offline Snapshot' : 'Live Data Feed'}</span>}
              <span>•</span>
              <span>{lastUpdated ?? '—'}</span>
            </div>
          </div>
          {onEditOrder && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/35">
              Click Edit or an order row to load it into Manual Order
            </span>
          )}
        </div>
      </div>

      <div className="relative">
        <OrderSymbolFilter orders={orders} value={symbolFilter} onChange={setSymbolFilter} />
        {snapshotStatus.stale && (
          <div className="mb-4 rounded-xl border border-warning-500/25 bg-warning-500/10 px-4 py-3 text-xs text-warning-300">
            {snapshotStatus.connectionStatus === 'initializing'
              ? 'IB is connected and open orders are synchronizing. This panel will refresh automatically.'
              : snapshotStatus.available
              ? 'Gateway offline — showing the last persisted open-order snapshot.'
              : 'Gateway offline — no persisted open-order snapshot is available yet.'}
          </div>
        )}
        {error && (
          <div className="mb-4">
            <ErrorDisplay
              error={error}
              onRetry={() => fetchOrders()}
              message={errorHeader}
            />
          </div>
        )}

        <DataTable
          data={displayOrders}
          columns={orderColumns}
          compact
          loading={loading}
          onRowClick={handleEditOrder}
          emptyMessage={snapshotStatus.stale && !snapshotStatus.available
            ? snapshotStatus.connectionStatus === 'initializing' ? "Synchronizing open orders…" : "No persisted open-order snapshot available"
            : "No open orders in the queue."}
          rowClassName={(order) => order.parent_id && order.parent_id !== 0 ? 'bg-white/[0.015]' : ''}
        />

        <div className="flex justify-end items-center gap-3 mt-6">
          <button
            className="px-6 py-2 rounded-xl border border-white/5 bg-white/5 text-[10px] font-bold text-muted hover:text-foreground hover:bg-white/10 disabled:opacity-30 transition-all uppercase tracking-widest active:scale-95 shadow-lg"
            disabled={page <= 1 || refreshing}
            onClick={() => {
              const nextPage = Math.max(1, page - 1);
              setPage(nextPage);
              fetchOrders(false, nextPage);
            }}
          >
            Previous
          </button>
          <div className="px-4 py-2 bg-black/40 rounded-xl border border-white/10 text-[10px] font-black text-neon shadow-[0_0_15px_rgba(0,255,153,0.1)]">
            {page.toString().padStart(2, '0')}
          </div>
          <button
            className="px-6 py-2 rounded-xl border border-white/5 bg-white/5 text-[10px] font-bold text-muted hover:text-foreground hover:bg-white/10 disabled:opacity-30 transition-all uppercase tracking-widest active:scale-95 shadow-lg"
            disabled={orders.length < pageSize || refreshing}
            onClick={() => {
              const nextPage = page + 1;
              setPage(nextPage);
              fetchOrders(false, nextPage);
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpenOrders;
